import { Link } from "react-router-dom";
import { Pages } from "@/constants/enums";
import { appPath } from "@/constants/paths";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { WidgetContentSkeleton } from "@/components/shared/loading-skeleton";
import { LogIn, ArrowLeft } from "lucide-react";
import { getRoleDisplayName } from "@/lib/auth-utils";
import { useRoleLabelMap } from "@/features/roles/hooks/useRolesQueries";
import type { UserActivityLoginsResult } from "@/features/reports/services/reportService";
import { formatOptionalArabicDateTime } from "@/lib/formatters";

interface RecentLoginsWidgetProps {
  data: UserActivityLoginsResult | undefined;
  isLoading: boolean;
}

export function RecentLoginsWidget({ data, isLoading }: RecentLoginsWidgetProps) {
  const roleLabelMap = useRoleLabelMap();
  const items = data?.items ?? [];

  return (
    <Card className="flex flex-col overflow-hidden" dir="rtl">
      <div className="accent-top-bar" />
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">آخر تسجيلات الدخول</CardTitle>
          <div className="rounded-md bg-primary/10 p-1.5 text-primary">
            <LogIn className="size-4" />
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 space-y-2">
        {isLoading ? (
          <WidgetContentSkeleton rows={2} />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            لا توجد تسجيلات دخول حديثة
          </p>
        ) : (
          items.map((user) => {
            const name =
              [user.firstName, user.lastName].filter(Boolean).join(" ") ||
              user.email;
            return (
              <div
                key={`${user.id}-${user.lastLoginAt ?? "unknown"}`}
                className="flex items-center justify-between gap-2 border-b border-border/60 py-2 text-sm last:border-0"
              >
                <div className="flex min-w-0 flex-1 items-center justify-start gap-2">
                  <span className="truncate font-medium text-foreground">
                    {name}
                  </span>
                  <Badge variant="outline" className="shrink-0 text-xs">
                    {getRoleDisplayName(user.role, roleLabelMap)}
                  </Badge>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatOptionalArabicDateTime(user.lastLoginAt)}
                </span>
              </div>
            );
          })
        )}
      </CardContent>

      <CardFooter className="justify-start pt-0">
        <Link
          to={`${appPath(Pages.ANALYTICS)}?tab=activity`}
          className="flex items-center gap-1 text-sm text-primary hover:text-primary/80"
        >
          عرض كل النشاط
          <ArrowLeft className="size-4" />
        </Link>
      </CardFooter>
    </Card>
  );
}
